/**
 * What to tell a person when the bill list or a report cannot be loaded.
 *
 * Decided from the machine-readable `code` the server stamps on a refusal (invariant 6), never
 * from its prose: a copy edit on the server must not change what this screen says, and a code
 * is never shown to the reader. A code this file does not know gets the general sentence.
 */

const MESSAGES: Record<string, string> = {
  DATE_RANGE_INVALID: 'One of the dates is not a real date. Pick it from the calendar again.',
  DATE_RANGE_REVERSED: 'The "from" date is after the "to" date. Swap them, or clear one.',
  AP_AMOUNT_FILTER_INVALID: 'An amount must be a number, zero or more.',
  AP_AMOUNT_RANGE_INVALID: 'The smallest amount is more than the largest. Swap them, or clear one.',
  AP_CURRENCY_INVALID: 'A currency is its three-letter code, such as TTD or USD.',
  AP_DUE_CHIP_INVALID: 'That due-date shortcut is not one this page knows. Clear the filters and try again.',
  AP_SORT_INVALID: 'That sort order is not one this page knows. Clear the filters and try again.',
  AP_SORT_DIR_INVALID: 'That sort direction is not one this page knows. Clear the filters and try again.',
  AP_HELD_INVALID: 'That on-hold filter is not one this page knows. Clear the filters and try again.',
  AP_FLAG_INVALID: 'One of the filters is not one this page knows. Clear the filters and try again.',
  AP_SUPPLIER_INVALID: 'That supplier name is too long to search for.',
  AGING_AS_OF_IN_PAST:
    'Balances are as they stand now; only the age is measured from the date you pick. Pick today or a later day.',
  DUE_SCHEDULE_RANGE_TOO_LONG: 'A due schedule covers at most a year. Choose a shorter range.',
  DUE_SCHEDULE_GROUP_INVALID: 'A schedule can be grouped by day or by week.',
};

const GENERAL = 'Something went wrong loading this. Please try again in a moment.';
const NO_ACCESS = 'You do not have access to Accounts Payable.';

/** A refusal the reader can fix by changing what they asked for (as opposed to a failure). */
export function isFixableByReader(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(MESSAGES, code);
}

export function apErrorMessage(error: unknown): string {
  const { code, status } = (error || {}) as { code?: unknown; status?: unknown };
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(MESSAGES, code)) return MESSAGES[code];
  if (status === 403) return NO_ACCESS;
  return GENERAL;
}
